import React from 'react';
import { Users, Heart, GraduationCap, Briefcase, TrendingUp } from 'lucide-react';
import { DataSourceBadge, DATA_SOURCES } from '../../ui/DataSourceBadge';
import { marketData } from '../../../data/terminalData';

interface DemographicAdvantagePanelProps {
  isExpanded?: boolean;
}

export const DemographicAdvantagePanel: React.FC<DemographicAdvantagePanelProps> = ({ isExpanded = false }) => {
  const { population } = marketData;

  const ageDistribution = [
    { range: '0-14', percent: 28, color: 'bg-data-blue' },
    { range: '15-29', percent: 27, color: 'bg-data-green' },
    { range: '30-44', percent: 22, color: 'bg-data-amber' },
    { range: '45-59', percent: 14, color: 'bg-gray-500' },
    { range: '60+', percent: 9, color: 'bg-gray-600' },
  ];

  return (
    <div className="bg-terminal-panel border border-terminal-border rounded-lg overflow-hidden h-full flex flex-col">
      {/* Header */}
      <div className="px-5 py-4 border-b border-terminal-border bg-terminal-bg/30 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users size={18} className="text-data-blue" />
          <h2 className="text-base font-bold text-white uppercase tracking-wide">Demographic Advantage</h2>
        </div>
        <span className="text-xs text-data-green font-semibold px-2 py-1 bg-data-green/10 rounded border border-data-green/20">
          {population.under30}% UNDER 30
        </span>
      </div>

      <div className="flex-1 p-5">
        {/* Population Hero */}
        <div className="bg-gradient-to-b from-data-blue/10 to-transparent rounded-lg p-4 border border-data-blue/20 mb-5">
          <div className="text-xs text-data-blue uppercase tracking-wider text-center mb-2">
            Population
          </div>
          <div className="text-center">
            <span className="text-5xl font-mono font-black text-white">{population.total}</span>
            <span className="text-2xl font-mono text-gray-500 ml-1">M</span>
          </div>
          <div className="flex items-center justify-center gap-2 mt-2">
            <TrendingUp size={14} className="text-data-green" />
            <span className="text-sm text-data-green font-semibold">+{population.growth}% YoY</span>
          </div>
          <div className="text-xs text-gray-500 text-center mt-1">#1 in Central Asia</div>
        </div>

        {/* Key Stats Row */}
        <div className="grid grid-cols-3 gap-3 mb-5">
          <div className="bg-terminal-bg/60 rounded-lg p-3 text-center border border-terminal-border/50">
            <Heart size={14} className="text-data-green mx-auto mb-1" />
            <div className="text-2xl font-mono font-bold text-white">{population.medianAge}</div>
            <div className="text-xs text-gray-500">Median Age</div>
            <div className="text-xs text-data-green">vs 38 EU</div>
          </div>
          <div className="bg-terminal-bg/60 rounded-lg p-3 text-center border border-terminal-border/50">
            <GraduationCap size={14} className="text-data-amber mx-auto mb-1" />
            <div className="text-2xl font-mono font-bold text-white">{population.under30}%</div>
            <div className="text-xs text-gray-500">Under 30</div>
            <div className="text-xs text-data-amber">Youth Bulge</div>
          </div>
          <div className="bg-terminal-bg/60 rounded-lg p-3 text-center border border-terminal-border/50">
            <Briefcase size={14} className="text-data-blue mx-auto mb-1" />
            <div className="text-2xl font-mono font-bold text-white">{population.laborForce}%</div>
            <div className="text-xs text-gray-500">Labor Force</div>
            <div className="text-xs text-data-blue">Participation</div>
          </div>
        </div>

        {/* Age Distribution Bar */}
        <div className="bg-terminal-bg/40 rounded-lg p-4 border border-terminal-border/30">
          <div className="text-xs text-gray-400 uppercase tracking-wider mb-3">Age Distribution</div>

          {/* Stacked Bar */}
          <div className="flex h-4 rounded-full overflow-hidden mb-3">
            {ageDistribution.map((age) => (
              <div
                key={age.range}
                className={`${age.color} transition-all`}
                style={{ width: `${age.percent}%` }}
                title={`${age.range}: ${age.percent}%`}
              />
            ))}
          </div>

          {/* Legend */}
          <div className="flex justify-between">
            {ageDistribution.map((age) => (
              <div key={age.range} className="text-center">
                <div className="text-xs font-mono font-semibold text-white">{age.percent}%</div>
                <div className="text-xs text-gray-500">{age.range}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="px-5 py-3 border-t border-terminal-border/50 flex items-center justify-between">
        <DataSourceBadge source={DATA_SOURCES.STAT_UZ} showLabel size="xs" />
        <span className="text-xs text-gray-500">Dec 2025</span>
      </div>
    </div>
  );
};

export default DemographicAdvantagePanel;
